/**
 * Macro — a single-slot performance control that plugs into the ModRouter
 * exactly like an LFO or S&H.
 *
 * The mechanism: a NumericParameter (0..1) bound to the `offset` AudioParam
 * of a ConstantSourceNode. The source node's audio output is the macro's
 * "mod signal" — flat DC at the current macro value. When the user moves
 * the macro knob, Parameter.bindAudioParam smooths the write with a short
 * time-constant, which means knob drags produce click-free mod signal.
 *
 * Per PLAN.md §11, a macro is a control-rate mod source that slots onto the
 * existing §5 ModConnection pipeline. No special code path — the router
 * treats it like any other preset-scope source.
 */

import type { AudioEngine } from '../audio/AudioEngine';
import { NumericParameter } from '../params/Parameter';

export class Macro {
  readonly id: string;
  readonly label: string;
  readonly value: NumericParameter;

  private source: ConstantSourceNode | null = null;

  constructor(index: number) {
    this.id = `macro-${index + 1}`;
    this.label = `M${index + 1}`;
    this.value = new NumericParameter({
      name: this.label,
      default: 0,
      min: 0,
      max: 1,
      curve: 'linear',
      smoothingMs: 20,
      // Macros are audible performance controls; randomizing them sweeps the
      // assigned targets, which is usually what users want. Bias toward the
      // low end so dice rolls don't always land at full throttle.
      randomSkew: 0.7,
      formatter: (v) => `${Math.round(v * 100)}%`,
    });
  }

  init(engine: AudioEngine): void {
    if (this.source) return;
    const ctx = engine.context;
    this.source = ctx.createConstantSource();
    this.source.offset.value = this.value.value;
    this.source.start();
    this.value.bindAudioParam(this.source.offset, ctx);
  }

  get modOutput(): AudioNode | null {
    return this.source;
  }

  dispose(): void {
    if (this.source) {
      try {
        this.source.stop();
      } catch {
        /* already stopped */
      }
      this.source.disconnect();
      this.source = null;
    }
  }
}
